import type { DocPage } from "../types";

export const page: DocPage = {
  slug: "accounts",
  title: "Portfolio accounts",
  navLabel: "Accounts",
  description:
    "Bring holdings into Mavori through a broker, an import, a Manual Account, or a Paper Account, then control how each account is used.",
  section: "portfolio-trading",
  published: "2026-08-03",
  updated: "2026-08-03",
  targets: [
    "add a Mavori portfolio account",
    "connect a broker to Mavori",
    "create a paper trading account",
  ],
  related: ["getting-started", "portfolio", "trading", "account"],
  blocks: [
    {
      type: "lead",
      text: "A Portfolio Account is the boundary for holdings, cash, valuation, Risk Controls, and Strategy Deployments. Add one by connecting a broker, importing holdings, recording a Manual Account, or creating a simulated Paper Account.",
    },

    { type: "h2", text: "Choose an account source" },
    {
      type: "table",
      caption: "Ways to bring a portfolio into Mavori",
      headers: ["Source", "Best for", "How it stays current"],
      rows: [
        [
          "Broker account",
          "Viewing broker-held positions and using supported trading permissions.",
          "Mavori reads the connected account when it synchronizes with the broker.",
        ],
        [
          "Imported account",
          "Starting from a brokerage screenshot, statement image, photo, or pasted position list.",
          "You confirm the extracted holdings, then maintain them as a Manual Account.",
        ],
        [
          "Manual Account",
          "Tracking positions or transactions that are not connected.",
          "You record holdings, cash activity, and trades.",
        ],
        [
          "Paper Account",
          "Practicing proposals and Strategy Deployments with simulated money.",
          "Mavori records simulated orders, fills, cash, and positions.",
        ],
      ],
    },

    { type: "h2", text: "Add the account" },
    {
      type: "steps",
      items: [
        {
          title: "Open Portfolio",
          text: "Choose the account-source action that matches the holdings you want to add.",
        },
        {
          title: "Connect, import, or create",
          text: "Complete the broker sign-in or credentials, confirm imported holdings, enter a Manual Account, or choose the starting cash for a Paper Account.",
        },
        {
          title: "Review the account",
          text: "Confirm its name, Live or Paper grouping, holdings, cash, valuation basis, and as-of information.",
        },
        {
          title: "Set Risk Controls",
          text: "Open the account's controls and activate the limits you want applied to trading activity.",
        },
      ],
    },

    { type: "h2", text: "Use accounts with the Agent" },
    {
      type: "p",
      text: "Select account context in the chat composer when you want the [Agent](/docs/agent) to reason from positions, orders, or account performance. If a turn needs another account, Mavori asks you to choose which accounts may be read for that conversation. Read access and permission to place an order are separate decisions.",
    },

    { type: "h2", text: "Manage account access" },
    {
      type: "p",
      text: "Manual and Paper Accounts expose their own cash and position controls on Portfolio. Broker connections are managed from **Settings → Connections**, where you can review connection state, trading access, halt orders, reauthorize, or disconnect. Removing a connection stops Mavori from using it; review the broker directly for any activity already there.",
    },
  ],
};
